import { formatToPriceString } from 'Util/util';
import VZSpinner from 'Components/VZSpinner';

export default class VZRangeSlider extends HTMLElement {
    constructor() {
        super();
    }

    /**
     * @function connectedCallback
     * @description Initializes the component and sets up event listeners for user input
     * @returns {void}
     */
    connectedCallback() {
        this.initializeElements();
        this.setupEventListeners();
        this.initSlider();
    }

    /**
     * @function disconnectedCallback
     * @description Removes event listeners for user input when the component is disconnected from the DOM
     * @returns {void}
     */
    disconnectedCallback() {
        this.removeEventListeners();
    }

    /**
     * @function initializeElements
     * @description Initialize all DOM elements and component properties
     */
    initializeElements() {
        // Input
        this.triggerInput = this.querySelector('.js-range-slider-input');

        this.minValue = this.triggerInput.getAttribute('min');
        this.maxValue = this.triggerInput.getAttribute('max');

        // Buttons
        this.decrementBtn = this.querySelector('.js-range-slider-decrease');
        this.incrementBtn = this.querySelector('.js-range-slider-increase');

        // Initialize spinner
        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();
    }

    /**
     * @function setupEventListeners
     * @description Set up all event listeners
     */
    setupEventListeners() {
        this.boundInputChange = this.onInputChange.bind(this);
        this.boundDecrementClick = this.onDecrementClick.bind(this);
        this.boundIncrementClick = this.onIncrementClick.bind(this);

        this.triggerInput.addEventListener('input', this.boundInputChange);
        this.decrementBtn.addEventListener('click', this.boundDecrementClick);
        this.incrementBtn.addEventListener('click', this.boundIncrementClick);
    }

    /**
     * @function removeEventListeners
     * @description Remove all event listeners
     */
    removeEventListeners() {
        this.triggerInput.removeEventListener('input', this.boundInputChange);
        this.decrementBtn.removeEventListener('click', this.boundDecrementClick);
        this.incrementBtn.removeEventListener('click', this.boundIncrementClick);
    }

    /**
     * @function initSlider
     * @description Initialize the slider with default values
     */
    initSlider() {
        this.updateSliderFill();
    }

    /**
     * @function updateSliderFill
     * @description Update the slider's fill visual
     */
    updateSliderFill() {
        const percentage = ((this.triggerInput.value - this.minValue) / (this.maxValue - this.minValue)) * 100;
        this.triggerInput.style.setProperty('--slider-filled-percentage', `${percentage}%`);
    }

    /**
     * @function onInputChange
     * @description Handle input changes
     * @param {Event} evt - The input event
     */
    onInputChange(evt) {
        this.updateSliderFill();
        this.dispatchSliderUpdate(evt.target.value);
    }

    /**
     * @function onDecrementClick
     * @description Handle decrement button click
     * @param {Event} evt - The click event
     */
    onDecrementClick(evt) {
        evt.preventDefault();
        const newValue = Math.max(this.minValue, parseInt(this.triggerInput.value, 10) - 1);
        this.updateValue(newValue);
    }

    /**
     * @function onIncrementClick
     * @description Handle increment button click
     * @param {Event} evt - The click event
     */
    onIncrementClick(evt) {
        evt.preventDefault();
        const newValue = Math.min(this.maxValue, parseInt(this.triggerInput.value, 10) + 1);
        this.updateValue(newValue);
    }

    /**
     * @function updateValue
     * @description Update the slider value
     * @param {number} value - The new value
     */
    updateValue(value) {
        if (this.triggerInput.value !== value.toString()) {
            this.triggerInput.value = value;
            this.triggerInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    /**
     * @function dispatchSliderUpdate
     * @description Dispatch a custom event when the slider value changes
     * @param {number} value - The new value
     */
    dispatchSliderUpdate(value) {
        const updateEvent = new CustomEvent('vzslider:update', {
            bubbles: true,
            detail: {
                value: value
            }
        });
        this.dispatchEvent(updateEvent);
    }

    /**
     * @function updatePriceDisplay
     * @description Update price displays in the slider
     * @param {Object} priceData - Object containing price information
     */
    updatePriceDisplay(priceData) {
        if (this.priceContainer && priceData.formattedPrice) {
            this.priceContainer.innerHTML = priceData.formattedPrice;
        }

        if (this.recurringPriceMsg && priceData.recurringPrice) {
            VZRangeSlider.updatePriceHtml(this.recurringPriceMsg, priceData.recurringPrice);
        }

        if (this.oneOffPriceMsg && priceData.oneOffPrice) {
            VZRangeSlider.updatePriceHtml(this.oneOffPriceMsg, priceData.oneOffPrice);
        }
    }

    /**
     * @function updatePriceHtml
     * @param {HTMLElement} elem - The element to update
     * @param {number|string} newPrice - The new price value
     */
    static updatePriceHtml(elem, newPrice) {
        const regex = /€.*/g;
        elem.innerHTML = elem.innerHTML.replace(regex, formatToPriceString(newPrice));
    }
}

if (!window.customElements.get('vz-range-slider')) {
    window.customElements.define('vz-range-slider', VZRangeSlider);
}
